
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { Stage, UserJourney } from "@/types/journey";
import { SavedJourney } from "./types";
import { getSavedJourneys, saveJourneys, getFolders } from "./storageUtils";

export const saveCurrentJourney = (
  stages: Stage[], 
  journey: UserJourney, 
  folderId: string | null = null
): void => {
  try {
    // Get existing saved journeys
    const savedJourneys = getSavedJourneys();
    
    // Create new journey entry
    const newJourney: SavedJourney = {
      id: uuidv4(),
      name: journey.mapName || "Untitled Journey Map",
      timestamp: new Date().toISOString(),
      journey,
      stages,
      folderId
    };
    
    // Add to list and save back to storage
    savedJourneys.push(newJourney);
    saveJourneys(savedJourneys);
    
    const folderName = folderId 
      ? getFolders().find(f => f.id === folderId)?.name 
      : null;
    
    if (folderName) {
      toast.success(`Saved "${newJourney.name}" to folder "${folderName}"`);
    } else {
      toast.success(`Saved "${newJourney.name}" to your notebooks`);
    }
  } catch (error) {
    console.error("Error saving journey:", error);
    toast.error("Failed to save journey map");
  }
};

export const moveJourneyToFolder = (journeyId: string, folderId: string | null): boolean => {
  try {
    const journeys = getSavedJourneys();
    const journeyIndex = journeys.findIndex(journey => journey.id === journeyId);
    
    if (journeyIndex === -1) {
      toast.error("Journey map not found");
      return false;
    }
    
    journeys[journeyIndex].folderId = folderId;
    saveJourneys(journeys);
    
    if (folderId) {
      const folder = getFolders().find(f => f.id === folderId);
      toast.success(`Moved to folder "${folder?.name || 'Unknown'}"`);
    } else {
      toast.success("Moved to root");
    }
    
    return true;
  } catch (error) {
    console.error("Error moving journey:", error);
    toast.error("Failed to move journey map");
    return false;
  }
};

export const loadJourney = (
  id: string,
  setStages: React.Dispatch<React.SetStateAction<Stage[]>>,
  setJourney: React.Dispatch<React.SetStateAction<UserJourney>>
): boolean => {
  try {
    const savedJourneys = getSavedJourneys();
    const journeyToLoad = savedJourneys.find(journey => journey.id === id);
    
    if (!journeyToLoad) {
      toast.error("Journey map not found");
      return false;
    }
    
    setStages(journeyToLoad.stages);
    setJourney(journeyToLoad.journey);
    
    toast.success(`Loaded "${journeyToLoad.name}"`);
    return true;
  } catch (error) {
    console.error("Error loading journey:", error);
    toast.error("Failed to load journey map");
    return false;
  }
};

export const deleteJourney = (id: string): boolean => {
  try {
    const savedJourneys = getSavedJourneys();
    const filteredJourneys = savedJourneys.filter(journey => journey.id !== id);
    
    if (filteredJourneys.length === savedJourneys.length) {
      toast.error("Journey map not found");
      return false;
    }
    
    saveJourneys(filteredJourneys);
    toast.success("Journey map deleted");
    return true;
  } catch (error) {
    console.error("Error deleting journey:", error);
    toast.error("Failed to delete journey map");
    return false;
  }
};
